import React, { useState, useEffect, useCallback } from 'react';

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { Box, Checkbox, TextField, Autocomplete } from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';

import useLocales from '../../../hooks/useLocales';
import { ElementProps } from '../../../@types/forms/types';
import './elements-vanilla-mobile/address-book-mobile.scss';
import { GroupedUsersProps } from '../fill-form/BuilderFormFiller';

//-------------------------------------------------------------

type AddressBookElementProps = {
  el: ElementProps;
  groupedUsers: GroupedUsersProps[];
  elementContentValue?: string[] | undefined;
  handleAddressBookChange: (value: GroupedUsersProps[], el: ElementProps) => void;
  pageNumberScale: number;
  collaboratorColor?: string;
  disabled?: boolean;
  errorMessage?: string;
};

const AddressBookElement: React.FC<AddressBookElementProps> = ({
  el,
  groupedUsers,
  elementContentValue,
  handleAddressBookChange,
  pageNumberScale,
  collaboratorColor,
  disabled = false,
  errorMessage,
}) => {
  const { t } = useLocales();
  const [value, setValue] = useState<{ firstLetter: string; name: string }[] | undefined>(
    undefined
  );
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  useEffect(() => {
    if (elementContentValue && elementContentValue.length > 0) {
      setValue(elementContentValue.map((u) => ({ firstLetter: u[0], name: u })));
    } else {
      setValue([]);
    }
  }, [elementContentValue]);

  const handleChange = useCallback(
    (_: React.SyntheticEvent, newValue: GroupedUsersProps[]) => {
      handleAddressBookChange(newValue, el);
      setValue(newValue);
    },
    [handleAddressBookChange, el]
  );

  return (
    <Box sx={{ m: 0, p: 0, width: '100%' }} id={el.id}>
      <Autocomplete
        multiple
        popupIcon={<ExpandMoreRoundedIcon />}
        value={value || []}
        limitTags={1}
        fullWidth
        disabled={disabled}
        sx={{
          backgroundColor: collaboratorColor,
          borderRadius: 1,
        }}
        id={`${el.id}`}
        onChange={handleChange}
        options={
          groupedUsers
            ? groupedUsers.sort((a, b) => a.firstLetter.localeCompare(b.firstLetter))
            : []
        }
        groupBy={(option) => option.firstLetter}
        disableCloseOnSelect
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.name === value.name}
        renderOption={(props, option, { selected }) => (
          <li {...props} key={`${props.id}`} className="li-line">
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 4 }}
              checked={selected}
            />
            {option.name}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            variant="standard"
            {...params}
            error={!!errorMessage}
            helperText={errorMessage || null}
            placeholder={t(`users.selectedUsers`)}
            id="0ahv1hiq"
          />
        )}
      />
    </Box>
  );
};

export default AddressBookElement;
